import { Machine, assign } from '@xstate/compiled';

interface Context {
  currencyBookId?: number | string;
  businessUnitId?: number;
  currency: DashboardCurrency;
}

export type DashboardCurrency = 'GBP' | 'USD' | 'EUR';

type Event =
  | {
      type: 'CHOOSE_CURRENCY_BOOK';
      id: number | string;
    }
  | {
      type: 'CHOOSE_SHOW_MODEL_BALANCES';
    }
  | {
      type: 'CHOOSE_HIDE_MODEL_BALANCES';
    }
  | {
      type: 'CHOOSE_BUSINESS_UNIT';
      id: number;
    }
  | {
      type: 'CHOOSE_ALL_CURRENCY_BOOKS';
    }
  | {
      type: 'CHOOSE_ALL_BUSINESS_UNITS';
    }
  | {
      type: 'CHOOSE_GBP_EUR_USD_CURRENCY_BOOKS';
    }
  | {
      type: 'UPDATE_CURRENCY';
      currency: DashboardCurrency;
    };

export const dashboardMachine = Machine<Context, Event, 'jcapDashboard'>(
  {
    id: 'dashboardMachine',
    context: {
      currency: 'GBP',
      currencyBookId: undefined,
    },
    type: 'parallel',
    states: {
      currency: {
        on: {
          UPDATE_CURRENCY: {
            actions: 'assignCurrencyToContext',
          },
        },
      },
      modelBalances: {
        initial: 'notShowing',
        states: {
          notShowing: {
            on: {
              CHOOSE_SHOW_MODEL_BALANCES: 'showing',
            },
          },
          showing: {
            on: {
              CHOOSE_HIDE_MODEL_BALANCES: 'notShowing',
            },
          },
        },
      },
      allCurrencyBookDefault: {
        initial: 'gbpEurUsd',
        states: {
          gbpEurUsd: {
            on: {
              CHOOSE_ALL_CURRENCY_BOOKS: {
                target: 'all',
              },
            },
          },
          all: {
            on: {
              CHOOSE_GBP_EUR_USD_CURRENCY_BOOKS: {
                target: 'gbpEurUsd',
              },
            },
          },
        },
      },
      filters: {
        initial: 'nothingSelected',
        states: {
          nothingSelected: {
            entry: ['clearBusinessUnit', 'clearCurrencyBook'],
            on: {
              CHOOSE_CURRENCY_BOOK: {
                target: 'currencyBookSelected',
                actions: ['assignCurrencyBook'],
              },
              CHOOSE_BUSINESS_UNIT: {
                target: 'businessUnitSelected',
                actions: ['assignBusinessUnit'],
              },
            },
          },
          businessUnitSelected: {
            entry: ['clearCurrencyBook'],
            on: {
              CHOOSE_ALL_BUSINESS_UNITS: {
                target: 'nothingSelected',
              },
              CHOOSE_BUSINESS_UNIT: {
                actions: ['assignBusinessUnit'],
              },
              CHOOSE_CURRENCY_BOOK: {
                target: 'currencyBookSelected',
                actions: ['assignCurrencyBook'],
              },
            },
          },
          currencyBookSelected: {
            on: {
              CHOOSE_ALL_BUSINESS_UNITS: {
                target: 'nothingSelected',
              },
              CHOOSE_ALL_CURRENCY_BOOKS: [
                {
                  cond: 'isBusinessUnitSelected',
                  target: 'businessUnitSelected',
                },
                {
                  target: 'nothingSelected',
                },
              ],
              CHOOSE_GBP_EUR_USD_CURRENCY_BOOKS: [
                {
                  cond: 'isBusinessUnitSelected',
                  target: 'businessUnitSelected',
                },
                {
                  target: 'nothingSelected',
                },
              ],
              CHOOSE_CURRENCY_BOOK: {
                actions: ['assignCurrencyBook'],
              },
              CHOOSE_BUSINESS_UNIT: {
                target: 'businessUnitSelected',
                actions: ['assignBusinessUnit'],
              },
            },
          },
        },
      },
    },
  },
  {
    guards: {
      isBusinessUnitSelected: (context) => {
        return Boolean(context.businessUnitId);
      },
    },
    actions: {
      clearBusinessUnit: assign((context) => ({
        businessUnitId: undefined,
      })),
      clearCurrencyBook: assign((context) => ({
        currencyBookId: undefined,
      })),
      assignBusinessUnit: assign((context, event) => {
        return {
          businessUnitId: event.id,
        };
      }),
      assignCurrencyBook: assign((context, event) => {
        return {
          currencyBookId: event.id,
        };
      }),
      assignCurrencyToContext: assign((context, event) => {
        return {
          currency: event.currency,
        };
      }),
    },
  },
);
